import { useForm, useFormState } from '@ff-it/form';
import { Box } from '@ff-it/ui';
import type { Activity, ActivityType } from 'types';
import {
  ChainTargetEntry,
  pathMatchesScope,
  ProductChainPicker,
  targetFromPath,
  targetIdFromPath,
} from 'modules/supplier/supplier/components/ProductChainPicker';
import type { ChainPath } from 'modules/supplier/supplier/components/ProductChainPicker';
import {
  DiscountRuleConditions,
  DiscountRuleDialogShell,
  DiscountRuleValues,
  mediaTargetInvalidReason,
} from 'modules/agreement/discountTree';
import { ruleValueSides, validateRule } from './form';
import type { ProviderTargetPath, RuleFormValues, RuleMutation } from './types';
import * as editorStyles from './ProviderAgreementDiscountEditor.css';
import * as dialogStyles from 'modules/agreement/discountTree/DiscountRuleDialog.css';

function RuleFields({
  activeType,
  targetOptions,
  scopePaths,
}: {
  activeType: ActivityType;
  targetOptions: readonly ProviderTargetPath[];
  scopePaths: readonly ChainPath[];
}) {
  const form = useForm<RuleFormValues>();
  const { values } = useFormState<RuleFormValues>({
    subscription: { values: true },
  });
  const selectedPath = values.target_path;
  const targetInvalid = selectedPath ? mediaTargetInvalidReason(selectedPath, activeType) : null;
  const selectedTarget = selectedPath ? targetFromPath(selectedPath) : null;
  const targetInCurrentScope = !selectedPath || pathMatchesScope(selectedPath, scopePaths);

  return (
    <>
      {targetInvalid && (
        <div className={dialogStyles.invalidNotice} role="alert">
          This target does not match {activeType}. Select a compatible media target before saving.
        </div>
      )}
      <div className={dialogStyles.layout}>
        <Box minWidth="0">
          <Box mb="2">
            <ChainTargetEntry
              value={selectedTarget}
              path={selectedPath ?? undefined}
              selectionStatus={selectedTarget && !targetInCurrentScope ? 'invalid' : undefined}
              emptyMessage="Select a vendor, outlet or product"
            />
          </Box>
          <div className={editorStyles.catalogue}>
            <ProductChainPicker
              className={editorStyles.chain}
              value={selectedTarget}
              path={selectedPath ?? undefined}
              paths={targetOptions}
              selectableKinds={['vendor', 'outlet', 'product']}
              onChange={(value) => {
                const selected = targetOptions.find((path) => {
                  const target = targetFromPath(path);
                  return target.id === value?.id && target.kind === value?.kind;
                });
                // Clicking the current endpoint is not a retarget and must not refresh saved labels.
                if (selected && (!selectedPath || targetIdFromPath(selected) !== targetIdFromPath(selectedPath))) {
                  form.change('target_path', selected);
                }
              }}
            />
          </div>
          <Box textSize="xs" color="secondary" mt="3">
            Choose a vendor, outlet or media product. Its full chain is saved with the rule.
          </Box>
        </Box>
        <Box minWidth="0" display="flex" flexDirection="column" gap="2">
          <DiscountRuleValues activeType={activeType} sides={ruleValueSides} />
          <DiscountRuleConditions
            activityKind="MEDIA"
            termsHelp="A blank column skips this rule for that context; zero matches with value 0."
          />
        </Box>
      </div>
    </>
  );
}

export type RuleDialogProps = {
  open: boolean;
  activeType: ActivityType;
  draft: RuleFormValues;
  editingRuleId?: number;
  activities: readonly Activity[];
  targetOptions: readonly ProviderTargetPath[];
  scopePaths: readonly ChainPath[];
  onSubmit: RuleMutation;
  onRemove?: () => Promise<void>;
  onClose: () => void;
};

export function RuleDialog({
  open,
  activeType,
  draft,
  editingRuleId,
  activities,
  targetOptions,
  scopePaths,
  onSubmit,
  onRemove,
  onClose,
}: RuleDialogProps) {
  const title = `${editingRuleId !== undefined ? 'Edit' : 'Add'} discount rule`;

  return (
    <DiscountRuleDialogShell<RuleFormValues>
      open={open}
      title={title}
      initialValues={draft}
      validate={(values) => validateRule(values, activities, targetOptions, activeType)}
      onSubmit={async (values) => {
        if (!values.target_path) return;
        return onSubmit({ ...values, target_path: values.target_path });
      }}
      onRemove={onRemove}
      onClose={onClose}
      removeLabel="Remove rule"
    >
      <RuleFields activeType={activeType} targetOptions={targetOptions} scopePaths={scopePaths} />
    </DiscountRuleDialogShell>
  );
}
